import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Input } from '../../components/ui/input';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from '../../components/ui/sheet';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  MapPin, Clock, Banknote, Coffee, Calendar, AlertCircle, Loader2, Search,
  X, Navigation, CalendarRange, CheckCircle2, ShieldCheck, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { getCoverageDisplay } from '../../utils/coverageDisplay';
import OutstandingPenaltiesBanner from '../../components/worker/OutstandingPenaltiesBanner';
import { formatApiError } from '../../lib/apiError';
import useGreeting from '../../hooks/useGreeting';

// Phase 5 UX (Feb 2026): "Find Shifts" is a worker's daily home. It now
// leads with a single question — what shifts are near me — and hides the
// city/area filters behind a "Change area" sheet so the primary card list
// is never buried. The apply flow was split into:
//   1. Tap "I'm available" on a card       (no immediate API call)
//   2. Confirmation dialog with shift facts (Confirm availability)
//   3. Full success dialog with "View My Shifts" (no silent return)
//
// The backend contract is unchanged — same POST /applications { shiftId }.

export default function AvailableShiftsPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const { timeGreeting, firstName, isFirstVisit } = useGreeting(user);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [locationHierarchy, setLocationHierarchy] = useState({});

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [cityFilter, setCityFilter] = useState('all');
  const [areaFilter, setAreaFilter] = useState('all');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const debounceRef = useRef(null);

  // Confirm / success dialog state
  const [confirmingShift, setConfirmingShift] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [successShift, setSuccessShift] = useState(null);

  const availableAreas = cityFilter !== 'all' && locationHierarchy[cityFilter]
    ? locationHierarchy[cityFilter]
    : [];

  // Location hierarchy is fetched once — used only inside the filter sheet.
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.get('/locations');
        if (mounted) setLocationHierarchy(res.data);
      } catch { /* silently ignore */ }
    })();
    return () => { mounted = false; };
  }, [api]);

  const fetchShifts = useCallback(async (search, city, area) => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (city && city !== 'all') params.append('city', city);
      if (area && area !== 'all') params.append('area', area);
      const url = params.toString() ? `/shifts/available?${params}` : '/shifts/available';
      const response = await api.get(url);
      setShifts(response.data);
    } catch {
      toast.error('Failed to load shifts');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    setLoading(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchShifts(searchQuery, cityFilter, areaFilter);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchQuery, cityFilter, areaFilter, fetchShifts]);

  const handleCityChange = (val) => {
    setCityFilter(val);
    setAreaFilter('all');
  };

  const openConfirmDialog = (shift) => {
    if (!user?.smartIdSelfDeclared && !user?.smartIdVerified) {
      toast.error('Please confirm your Smart ID status from your Profile before applying.');
      return;
    }
    setConfirmingShift(shift);
  };

  const submitAvailability = async () => {
    if (!confirmingShift) return;
    setSubmitting(true);
    try {
      await api.post('/applications', { shiftId: confirmingShift.id });
      setShifts(prev => prev.filter(s => s.id !== confirmingShift.id));
      setSuccessShift(confirmingShift);
      setConfirmingShift(null);
    } catch (error) {
      toast.error(formatApiError(error, 'This shift is no longer available.'));
    } finally {
      setSubmitting(false);
    }
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setCityFilter('all');
    setAreaFilter('all');
  };

  const hasFilters = searchQuery || cityFilter !== 'all' || areaFilter !== 'all';
  const cities = Object.keys(locationHierarchy).sort();

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    const today = new Date();
    const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
    return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  const displayLocation = (() => {
    const parts = [];
    if (cityFilter !== 'all') parts.push(cityFilter);
    if (areaFilter !== 'all') parts.push(areaFilter);
    if (parts.length > 0) return parts.join(' · ');
    return user?.postcode || 'your area';
  })();

  if (loading && shifts.length === 0 && !hasFilters) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-16 w-full" />
        {[1, 2, 3].map(i => <Skeleton key={i} className="h-48" />)}
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-5" data-testid="available-shifts-page">
      {/* Greeting hero — iter97 */}
      <section data-testid="worker-hero">
        <h1
          className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight font-['Public_Sans']"
          data-testid="worker-greeting"
        >
          {timeGreeting}, {firstName}
        </h1>
        <p
          className="text-base sm:text-lg text-slate-600 mt-1"
          data-testid="worker-greeting-subtitle"
        >
          {isFirstVisit ? "Let's find your next shift." : 'Welcome back.'}
        </p>
      </section>

      {/* Section heading kept for context — screen title is still Find Shifts. */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900" data-testid="find-shifts-heading">Find Shifts</h2>
        <p className="text-slate-500 mt-1 text-sm" data-testid="find-shifts-sub">Shifts available near you.</p>
      </div>

      <OutstandingPenaltiesBanner />

      {/* Location row: "Near [postcode]" + "Change area" primary chip. */}
      <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="location-row">
        <div className="inline-flex items-center gap-2 text-slate-700">
          <Navigation className="h-4 w-4 text-slate-400" />
          <span className="text-base">Near <span className="font-semibold">{displayLocation}</span></span>
        </div>
        <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
          <SheetTrigger asChild>
            <Button
              variant="outline"
              className="min-h-[44px] rounded-full text-sm font-medium"
              data-testid="change-area-btn"
            >
              Change area
            </Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="rounded-t-2xl">
            <SheetHeader>
              <SheetTitle>Change area</SheetTitle>
            </SheetHeader>
            <div className="mt-4 space-y-4" data-testid="change-area-panel">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  placeholder="Search by postcode or branch"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="pl-12 h-12 rounded-xl text-base"
                  data-testid="shift-search-input"
                />
              </div>
              <Select value={cityFilter} onValueChange={handleCityChange}>
                <SelectTrigger className="h-12 rounded-xl text-base" data-testid="city-filter">
                  <div className="flex items-center gap-2"><Navigation className="h-4 w-4 text-slate-400" /><SelectValue placeholder="All cities" /></div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All cities</SelectItem>
                  {cities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select key={`area-${cityFilter}`} value={areaFilter} onValueChange={setAreaFilter}>
                <SelectTrigger className="h-12 rounded-xl text-base" data-testid="area-filter">
                  <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" /><SelectValue placeholder={cityFilter === 'all' ? 'Select a city first' : 'All areas'} /></div>
                </SelectTrigger>
                <SelectContent>
                  {cityFilter === 'all' ? (
                    <SelectItem value="all" className="text-slate-400">Select a city first</SelectItem>
                  ) : (
                    <>
                      <SelectItem value="all">All areas</SelectItem>
                      {availableAreas.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </>
                  )}
                </SelectContent>
              </Select>
              <div className="flex gap-2 pt-2">
                {hasFilters && (
                  <Button variant="outline" className="flex-1 h-12 rounded-full" onClick={clearAllFilters} data-testid="clear-all-filters-btn">
                    Clear
                  </Button>
                )}
                <Button className="flex-1 h-12 rounded-full bg-[#DA291C] hover:bg-[#b91f13] text-white" onClick={() => setFiltersOpen(false)} data-testid="apply-filters-btn">
                  Done
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {/* Verification warning (Smart ID not declared) */}
      {!user?.smartIdSelfDeclared && !user?.smartIdVerified && (
        <Card className="border-0 shadow-sm bg-amber-50">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-amber-800 text-sm leading-snug">
              <span className="font-semibold block">Smart ID status not confirmed</span>
              Update it from your Profile before applying.
            </p>
          </CardContent>
        </Card>
      )}

      <p className="text-sm text-slate-500" data-testid="shift-count">
        {loading ? 'Searching…' : `${shifts.length} shift${shifts.length !== 1 ? 's' : ''} available`}
      </p>

      {/* Shift list or empty state */}
      {!loading && shifts.length === 0 ? (
        <Card className="border-0 shadow-sm" data-testid="empty-state">
          <CardContent className="p-10 text-center">
            <Calendar className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            {hasFilters ? (
              <>
                <h3 className="text-lg font-medium text-slate-900 mb-1">No shifts match those filters.</h3>
                <p className="text-slate-500 mb-4">Try broadening your search.</p>
                <Button variant="outline" onClick={clearAllFilters} className="rounded-full min-h-[44px]">Clear filters</Button>
              </>
            ) : (
              <>
                <h3 className="text-lg font-medium text-slate-900 mb-1" data-testid="empty-title">No shifts available right now.</h3>
                <p className="text-slate-500" data-testid="empty-sub">We&apos;ll show new shifts here when managers post them.</p>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {shifts.map((shift) => {
            const canApply = user?.smartIdSelfDeclared || user?.smartIdVerified;
            return (
              <Card key={shift.id} className="border-0 shadow-sm hover:shadow-md transition-shadow" data-testid={`shift-card-${shift.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3 gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-lg font-semibold text-slate-900 truncate">{shift.branchName}</h3>
                        {shift.isDemo && (
                          <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-xs font-semibold" data-testid={`demo-shift-badge-${shift.id}`}>Demo</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-slate-500 mt-1">
                        <MapPin className="h-4 w-4 shrink-0" />
                        <span className="text-sm truncate">{shift.location}</span>
                      </div>
                      {(shift.postcode || shift.area) && (
                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                          {shift.postcode && (
                            <Badge variant="outline" className="text-xs font-mono font-medium text-slate-600 border-slate-200 bg-slate-50 px-2 py-0.5">{shift.postcode}</Badge>
                          )}
                          {shift.area && <span className="text-xs text-slate-400">{shift.city && `${shift.city} · `}{shift.area}</span>}
                        </div>
                      )}
                    </div>
                    <Badge className="bg-emerald-100 text-emerald-800 font-semibold whitespace-nowrap">
                      {typeof shift.pay === 'number' && shift.pay > 0 ? `£${shift.pay.toFixed(2)}` : '£TBC'}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 py-3 px-4 bg-slate-50 rounded-xl text-sm">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Date</p>
                        <p className="font-medium text-slate-900">{formatDate(shift.date)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Time</p>
                        <p className="font-medium text-slate-900">{shift.scheduledStartTime} - {shift.scheduledEndTime}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Coffee className="h-4 w-4 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Break</p>
                        <p className="font-medium text-slate-900">{shift.breakDurationMinutes} min</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Banknote className="h-4 w-4 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Pay</p>
                        <p className="font-medium text-slate-900">
                          {typeof shift.pay === 'number' && shift.pay > 0 ? `£${shift.pay.toFixed(2)}` : 'TBC'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {(shift.experienceRequired || shift.smartIdRequired) && (
                    <div className="flex items-center flex-wrap gap-2 mb-3 text-sm" data-testid={`requirements-${shift.id}`}>
                      {shift.experienceRequired && (
                        <Badge className="bg-blue-50 text-blue-800 font-medium">Experience: {shift.experienceRequired}</Badge>
                      )}
                      {shift.smartIdRequired && (
                        <Badge className="bg-emerald-50 text-emerald-800 font-medium inline-flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> Smart ID required</Badge>
                      )}
                    </div>
                  )}

                  {shift.notes && <p className="text-sm text-slate-600 mb-3 italic">&quot;{shift.notes}&quot;</p>}

                  {(() => {
                    const cov = getCoverageDisplay(shift);
                    if (!cov) return null;
                    return (
                      <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-blue-50 rounded-lg" data-testid={`coverage-badge-${shift.id}`}>
                        <CalendarRange className="h-4 w-4 text-blue-600 shrink-0" />
                        <span className="text-sm font-medium text-blue-800">{cov.full}</span>
                      </div>
                    );
                  })()}

                  <Button
                    className="w-full h-14 rounded-full bg-[#DA291C] hover:bg-[#b91f13] text-white font-semibold text-base"
                    onClick={() => openConfirmDialog(shift)}
                    disabled={!canApply}
                    data-testid={`apply-btn-${shift.id}`}
                  >
                    {canApply ? "I'm available" : 'Confirm Smart ID first'}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Confirmation dialog */}
      <Dialog open={!!confirmingShift} onOpenChange={(open) => { if (!open) setConfirmingShift(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="confirm-availability-dialog">
          <DialogHeader>
            <DialogTitle>You&apos;re confirming that you&apos;re available for this shift.</DialogTitle>
            <DialogDescription>
              The manager will review your request and let you know if you&apos;re chosen.
            </DialogDescription>
          </DialogHeader>
          {confirmingShift && (
            <div className="rounded-xl border border-slate-200 divide-y divide-slate-200" data-testid="confirm-details">
              <div className="p-3.5 flex items-center justify-between text-base">
                <span className="text-slate-500 font-medium">Date</span>
                <span className="font-semibold text-slate-900" data-testid="confirm-date">{formatDate(confirmingShift.date)}</span>
              </div>
              <div className="p-3.5 flex items-center justify-between text-base">
                <span className="text-slate-500 font-medium">Time</span>
                <span className="font-semibold text-slate-900" data-testid="confirm-time">{confirmingShift.scheduledStartTime}–{confirmingShift.scheduledEndTime}</span>
              </div>
              <div className="p-3.5 flex items-center justify-between text-base">
                <span className="text-slate-500 font-medium">Branch</span>
                <span className="font-semibold text-slate-900 text-right truncate max-w-[60%]" data-testid="confirm-branch">{confirmingShift.branchName}</span>
              </div>
              <div className="p-3.5 flex items-center justify-between text-base">
                <span className="text-slate-500 font-medium">Pay</span>
                <span className="font-semibold text-slate-900" data-testid="confirm-pay">£{Number(confirmingShift.pay || 0).toFixed(2)}</span>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" className="rounded-full min-h-[48px]" onClick={() => setConfirmingShift(null)} disabled={submitting} data-testid="confirm-cancel-btn">
              Not yet
            </Button>
            <Button
              className="rounded-full min-h-[48px] bg-[#DA291C] hover:bg-[#b91f13] text-white"
              onClick={submitAvailability}
              disabled={submitting}
              data-testid="confirm-submit-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm availability'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Success dialog — full stop instead of silent return to the list */}
      <Dialog open={!!successShift} onOpenChange={(open) => { if (!open) setSuccessShift(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="availability-success-dialog">
          <DialogHeader className="sr-only">
            <DialogTitle>Your availability has been sent.</DialogTitle>
            <DialogDescription>Your availability request was submitted to the manager for review.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center text-center py-3">
            <div className="h-14 w-14 rounded-2xl bg-emerald-600 flex items-center justify-center mb-4">
              <CheckCircle2 className="h-7 w-7 text-white" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-1.5" data-testid="availability-success-title">Your availability has been sent.</h3>
            <p className="text-base text-slate-600 mb-1">The manager will review your request.</p>
            <p className="text-base text-slate-600 mb-6">We&apos;ll let you know when they decide.</p>
            <div className="w-full flex flex-col gap-2">
              <Button
                className="w-full min-h-[48px] rounded-full bg-[#0f1b3d] hover:bg-[#1a2d5a] text-white text-base font-semibold"
                onClick={() => { setSuccessShift(null); navigate('/worker/my-shifts?tab=waiting'); }}
                data-testid="success-view-my-shifts-btn"
              >
                View My Shifts <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
              <Button
                variant="ghost"
                className="w-full min-h-[48px] rounded-full text-base text-slate-500 hover:text-slate-900"
                onClick={() => setSuccessShift(null)}
                data-testid="success-keep-browsing-btn"
              >
                Keep browsing
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
